<template>
  <component-wrapper
    ref="wrapperRef"
    class="salesforce-form-fi"
  >
    <grid-container-fi>
      <component
        :is="isCustomElement"
        ref="salesforceForm"
        :title="fields.title?.value"
        class="salesforce-form-fi__container"
        data-recaptcha-site-key=""
        data-web-tracking="true"
        data-external-form="true"
      />
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useNotificationStore } from 'store/notification';
import { computed, onMounted, onUnmounted, useTemplateRef } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useFields } from '@/composables/fields';

defineOptions({
  name: 'SalesforceFormFi',
});

const { openGeneralErrorNotification } = useNotificationStore();
const fields = useFields('SalesforceFormFiFields');
const wrapperRef = useTemplateRef<InstanceType<typeof ComponentWrapper>>('wrapperRef');
const salesforceFormRef = useTemplateRef<HTMLElement>('salesforceForm');
let observer: IntersectionObserver | null = null;

const isCustomElement = computed(() => {
  if (!fields.containerId?.value) {
    console.error('container id missing');
    openGeneralErrorNotification();
    return 'div';
  }

  return `fragment-${fields.containerId?.value}`;
});

// TODO: ADD PROD URL IF KNOWN
const getLink = (scriptId: string) => {
  if (scriptId === 'salesforce-form-script') {
    return APP_ENVIRONMENT === 'PROD'
      ? 'https://fischer-group.my.site.com/lp/assets/scripts/external-forms-host.min.js'
      : 'https://computing-customization-8461--fischerqa.sandbox.my.site.com/lp/assets/scripts/external-forms-host.min.js';
  }

  if (!fields.filename?.value) {
    console.error('filename missing');
    openGeneralErrorNotification();
    return '';
  }

  return APP_ENVIRONMENT === 'PROD'
    ? `https://fischer-group.my.site.com/lp/assets/lightning_out_embed/prod/en-US/${fields.filename.value}`
    : `https://computing-customization-8461--fischerqa.sandbox.my.site.com/lp/assets/lightning_out_embed/prod/en-US/${fields.filename.value}`;
};

const load = () => {
  const parentNode = salesforceFormRef.value?.parentNode;
  if (!parentNode) {
    console.error('SalesforceFormFi: parentNode not found');
    openGeneralErrorNotification();
    return;
  }

  if (!document.getElementById('salesforce-form-script')) {
    const script = document.createElement('script');
    script.async = true;
    script.id = 'salesforce-form-script';
    script.src = getLink(script.id);
    parentNode.insertBefore(script, salesforceFormRef.value);
  }

  if (!document.getElementById('salesforce-form-script-2')) {
    const script = document.createElement('script');
    script.async = true;
    script.id = 'salesforce-form-script-2';
    script.src = getLink(script.id);
    script.onload = () => {
      const w = window as typeof window & { sfdc_forms?: { init: () => void } };
      if (w.sfdc_forms) {
        w.sfdc_forms.init();
      } else {
        w.sfdc_forms = { loReady: true } as unknown as { init: () => void };
      }
    };
    parentNode.insertBefore(script, salesforceFormRef.value);
  }
};

onMounted(() => {
  const el = wrapperRef.value?.$el as HTMLElement | undefined;
  if (!el) {
    load();
    return;
  }

  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        load();
        observer?.disconnect();
        observer = null;
      }
    },
    { rootMargin: '200px' },
  );

  observer.observe(el);
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<style scoped src="./salesforce-form-fi" />
